'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button } from '@/components/Button';
import { ZipCity, type ZipCityLocation } from '@/components/ZipCity';
import { useLocationFinder } from '@/lib/useLocationFinder';

interface PlzFinderProps {
  /**
   * Render the "Standort finden" heading and its explanatory line above the
   * input. The Navigation Grid card needs them — its cards carry no body text
   * of their own. The Flexible Content Card does not: it already has a kicker,
   * a title and a rich text field, so a second heading inside it would read as
   * a duplicate (SH-419).
   */
  withIntro?: boolean;
  /**
   * `stacked` (default) puts the button under a full-width input, which is what
   * the Navigation Grid card has always shown. `split` lays the two out as one
   * row sharing the width equally, for the Flexible Content Card (SH-419).
   */
  layout?: 'stacked' | 'split';
}

// Shared between the Navigation Grid card and the Flexible Content Card, so a
// visitor meets the same finder in both. The `navigationGrid` message
// namespace is kept deliberately — the keys are unchanged and renaming them
// would be churn.
export function PlzFinder({
  withIntro = true,
  layout = 'stacked',
}: PlzFinderProps) {
  const split = layout === 'split';
  const t = useTranslations('navigationGrid');
  const { query, handleValueChange, handleSelect, handleSubmit } =
    useLocationFinder();
  // The separate Button below sits outside `<ZipCity>`, so it has no access
  // to the dropdown's own live results — `onResultsChange` is how it stays
  // current with what the visitor is actually looking at (same pattern as
  // `ZipCityButton`).
  const [liveResults, setLiveResults] = useState<ZipCityLocation[]>([]);

  return (
    <div className="mt-4">
      {withIntro && (
        <>
          <p className="h4 mb-2 text-base font-medium">{t('plzFinderTitle')}</p>
          <p className="text-foreground/80 mb-4 text-sm">
            {t('plzFinderDescription')}
          </p>
        </>
      )}
      <div
        className={classNames(
          'gap-3 pb-4',
          split
            ? // `grid-cols-2` is `repeat(2, minmax(0, 1fr))`, so the two halves
              // are exactly equal whatever the button label's intrinsic width
              // — `flex-1` is not equivalent here, because a flex item's
              // automatic minimum size still floors it at its content width.
              // `--input-height` is what `<Button>`'s height resolves against
              // (it falls back to 55px, and nothing defines it globally), so
              // setting it here matches the button to the 50px input without
              // two competing height utilities.
              'grid grid-cols-2 items-center [--input-height:50px]'
            : 'flex flex-col',
        )}
      >
        <div className={split ? 'min-w-0' : undefined}>
          <ZipCity
            value={query}
            onValueChange={handleValueChange}
            onSelect={handleSelect}
            onSubmit={handleSubmit}
            onResultsChange={setLiveResults}
          />
        </div>
        <Button
          variant="primary"
          onClick={() => handleSubmit(query, liveResults)}
          className={classNames('text-lg', split ? 'w-full min-w-0' : 'w-full')}
        >
          {t('plzFinderButton')}
        </Button>
      </div>
    </div>
  );
}
